import { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';

import Login, { AuthAPI, useAuth } from '@qualitas/auth';

import AppLayout from './Components/AppLayout/AppLayout';
import { TranslationsAPI } from './Api/TranslationsAPI';
import { TranslationDictionary, TranslationLanguage, TranslationsConfiguration } from './Models';

import './App.scss';

const baseURL = process.env['REACT_APP_WebAPI_BaseURL'];
const baseURLExternal = process.env['REACT_APP_WebAPI_BaseURLExternal'];
const App = () => {
	const navigate = useNavigate();
	const client_id = 'bookbuilding';
	
	const [language, setLanguage] = useState(navigator.language.split("-")[0].toLowerCase());
	const [translationsConfiguration, setTranslationsConfiguration] = useState<TranslationsConfiguration | null>(null);
	const [translationsLoading, setTranslationsLoading] = useState(true);
	const [isInvestor, setIsInvestor] = useState<boolean>(true);
	const translationsAPI = useMemo(() => TranslationsAPI(baseURL, client_id), []);

	const resolveSupportedLanguage = useCallback((nextLanguage: string, config: TranslationsConfiguration | null) => {
		if (!config || config.languages.length === 0) {
			return nextLanguage;
		}

		const normalizedLanguage = (nextLanguage || '').trim().toLowerCase();
		const supportedLanguage = config.languages.find(item => item.code.toLowerCase() === normalizedLanguage);
		return supportedLanguage?.code
			?? config.defaultLanguage
			?? config.languages[0].code;
	}, []);

	const loadTranslations = useCallback(async () => {
		setTranslationsLoading(true);
		try {
			const response = await translationsAPI.getTranslations();
			if (!response?.success || !response?.data) {
				throw new Error(response?.message || 'Unable to load translations');
			}

			const nextConfiguration = response.data as TranslationsConfiguration;
			setTranslationsConfiguration(nextConfiguration);
			setLanguage(currentLanguage => resolveSupportedLanguage(currentLanguage, nextConfiguration));
		} catch (error) {
			console.error(error);
		} finally {
			setTranslationsLoading(false);
		}
	}, [resolveSupportedLanguage, translationsAPI]);

	const saveTranslations = useCallback(async (nextConfiguration: TranslationsConfiguration) => {
		const response = await translationsAPI.saveTranslations(nextConfiguration);
		if (!response?.success || !response?.data) {
			throw new Error(response?.message || 'Unable to save translations');
		}

		const savedConfiguration = response.data as TranslationsConfiguration;
		setTranslationsConfiguration(savedConfiguration);
		setLanguage(currentLanguage => resolveSupportedLanguage(currentLanguage, savedConfiguration));
		return savedConfiguration;
	}, [resolveSupportedLanguage, translationsAPI]);

	const createTranslationLanguage = useCallback(async (languageToCreate: TranslationLanguage) => {
		const response = await translationsAPI.createLanguage(languageToCreate);
		if (!response?.success || !response?.data) {
			throw new Error(response?.message || 'Unable to create language');
		}

		const savedConfiguration = response.data as TranslationsConfiguration;
		setTranslationsConfiguration(savedConfiguration);
		setLanguage(currentLanguage => resolveSupportedLanguage(currentLanguage, savedConfiguration));
		return savedConfiguration;
	}, [resolveSupportedLanguage, translationsAPI]);

	useEffect(() => {
		loadTranslations();
	}, [loadTranslations]);

	const translations = useMemo<TranslationDictionary>(() => {
		if (!translationsConfiguration) {
			return {};
		}

		const currentLanguage = resolveSupportedLanguage(language, translationsConfiguration);
		return translationsConfiguration.translations[currentLanguage]
			?? translationsConfiguration.translations[translationsConfiguration.defaultLanguage]
			?? {};
	}, [language, resolveSupportedLanguage, translationsConfiguration]);

	const realm =  process.env['REACT_APP_Realm'];
	const authAPI = AuthAPI(baseURLExternal, client_id);
	const authProps = useAuth({
		api: authAPI,
		navigate,
		redirectUrl: '/',
		systemMasterName: 'Bookbuilding',
		redirectPassResetUrl: process.env['REACT_APP_Pass_Reset'],
		language,
		client_id,
		realm
	});

	useEffect(() => {
		if (authProps?.userId > 0 ) {
			setIsInvestor(authProps?.userType === 'Investor');
		}
	}, [authProps?.userId]);

	return (
		<Routes>
			<Route path="/login" element={
				<Login {...authProps}
					appName="Bookbuilding"
				/>
			}/>
			<Route path="*" element={
				<AppLayout
					clientId={client_id}
					baseURL={baseURL}
					baseURLExternal={baseURLExternal}
					validateAuthToken={authProps.validateAuthToken}
					language={language}
					setLanguage={setLanguage}
					logout={authProps.logOut}
					userId={authProps.userId}
					userContactId={authProps.userContactId}
					userEmail={authProps.userEmail}
					userLogo={authProps.userLogo}
					userPrimaryColor={authProps.userPrimaryColor}
					userIdInstitution={Number(authProps.userIdInstitution)}
					sendResetPasswordEmail={authProps.sendResetPasswordEmail}
					translations={translations}
					availableLanguages={translationsConfiguration?.languages ?? []}
					translationsConfiguration={translationsConfiguration}
					saveTranslations={saveTranslations}
					createTranslationLanguage={createTranslationLanguage}
					translationsLoading={translationsLoading}
					isInvestor={isInvestor}
					moduleRole={authProps.moduleRole}
				/>
			}/>
		</Routes>
	);
};

export default App;
